import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack } from '@mui/material'
import { createTeam } from 'api/teams'
import FormInput from 'components/form-elements/FormInput'
import FormSelect from 'components/form-elements/FormSelect'
import { FIELD_IS_REQUIRED } from 'const'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { TeamType, TeamTypeOptions } from 'types'
import zod, { TypeOf } from 'zod'
import { useTeams } from '../../hooks/useTeams'

export function TimelineTeamFormAdd({ onClose }: { onClose: () => void }) {
  const { onRefetchTeams } = useTeams()

  const formSchema = zod.object({
    name: zod.string().min(1, FIELD_IS_REQUIRED).max(50, 'Please enter a maximum of 50 characters'),
    type: zod.string(),
  })

  type FormType = TypeOf<typeof formSchema>

  const methods = useForm<FormType>({
    mode: 'onChange' as const,
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: TeamType.Individual,
    },
  })

  const createMutation = useMutation(createTeam, {
    onSuccess: () => {
      onRefetchTeams()
      onClose()
    },
  })

  const onSubmitHandler = async (data: FormType) => {
    try {
      await createMutation.mutateAsync({
        name: data.name,
        type: data.type as TeamType,
      })
    } catch (error) {
      console.error('Error creating team:', error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onSubmitHandler)}
        width="100%"
        noValidate
      >
        <Stack spacing={2}>
          <FormInput label="Name" name="name" fullWidth={true} />

          <FormSelect name="type" label="Type" fullWidth options={TeamTypeOptions} />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>

            <LoadingButton loading={createMutation.isLoading} variant="contained" type="submit">
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </FormProvider>
  )
}
